import { parsePhoneNumber } from 'awesome-phonenumber';

import { COUNTRY_CALLING_CODES } from 'src/common/constants';
import { countryCodeAndNameMap, CountryCodes } from 'src/common/enums';
import { isWhatsAppEnabled } from 'src/common/hooks/useCountryConfig';
import { CALLING_CODES } from 'src/modules/Settings/Constants';

function isValidIndonesianPhoneNumber(
  phoneNumber: string,
  validateCountryCode: boolean
): boolean {
  const regexWithoutCode = /^8[0-9]{8,11}$/;
  const regexWithCode = new RegExp(`^\\+62${regexWithoutCode.source.slice(1)}`);
  return validateCountryCode
    ? regexWithCode.test(phoneNumber)
    : regexWithoutCode.test(phoneNumber);
}

/**
 * Requires +65 at the start, with optional space.
 * Mobile phone starts with 8 or 9.
 * 8 Digits in total (Not including +65).
 */
function isValidSingaporePhoneNumber(
  phoneNumber: string,
  validateCountryCode: boolean
): boolean {
  const regexWithoutCode = /^(?:8\d{7}|9\d{7})$/;
  const regexWithCode = new RegExp(
    `^\\+65\\s?${regexWithoutCode.source.slice(1)}`
  );
  return validateCountryCode
    ? regexWithCode.test(phoneNumber)
    : regexWithoutCode.test(phoneNumber);
}

/**
 * Requires +60 at the start, with optional space.
 * Mobile phone starts with 1.
 * 8 or 9 Digits in total (Not including +60).
 */
function isValidMalaysianPhoneNumber(
  phoneNumber: string,
  validateCountryCode: boolean
): boolean {
  const regexWithoutCode = /^1\d{8,9}$/;
  const regexWithCode = new RegExp(
    `^\\+60\\s?${regexWithoutCode.source.slice(1)}`
  );
  return validateCountryCode
    ? regexWithCode.test(phoneNumber)
    : regexWithoutCode.test(phoneNumber);
}

export function validatePhoneNumber(value: {
  countryCode: CountryCodes;
  phoneNumber?: string;
  validateCountryCode?: boolean;
}): boolean {
  if (!value.phoneNumber) return false;
  const validateCountryCode = value.validateCountryCode ?? true;

  switch (value.countryCode) {
    case CountryCodes.SG:
      return isValidSingaporePhoneNumber(
        value.phoneNumber,
        validateCountryCode
      );
    case CountryCodes.ID:
      return isValidIndonesianPhoneNumber(
        value.phoneNumber,
        validateCountryCode
      );
    case CountryCodes.MY:
      return isValidMalaysianPhoneNumber(
        value.phoneNumber,
        validateCountryCode
      );
    case CountryCodes.VN:
    case CountryCodes.TW:
      return parsePhoneNumber(value.phoneNumber).valid;
  }
}

export function getCallingCode(countryCode: CountryCodes) {
  switch (countryCode) {
    case CountryCodes.SG:
      return CALLING_CODES.SG;
    case CountryCodes.ID:
      return CALLING_CODES.ID;
    case CountryCodes.VN:
      return CALLING_CODES.VN;
    case CountryCodes.MY:
      return CALLING_CODES.MY;
    case CountryCodes.TW:
      return CALLING_CODES.TW;
  }
}

export interface WhatsAppCallingCodeOption {
  label: string;
  callingCode: number;
  countryCode: CountryCodes;
}

export const getWhatsAppEnabledCallingCodeOptions =
  (): WhatsAppCallingCodeOption[] =>
    (Object.values(CountryCodes) as CountryCodes[])
      .filter(isWhatsAppEnabled)
      .map((countryCode) => {
        const callingCodeStr =
          COUNTRY_CALLING_CODES[
            countryCode.toLowerCase() as keyof typeof COUNTRY_CALLING_CODES
          ];
        return {
          label: countryCodeAndNameMap[countryCode],
          callingCode: Number(callingCodeStr?.replace('+', '') ?? 0),
          countryCode,
        };
      });

export function getCountryCodeFromCallingCode(
  callingCode: string
): CountryCodes {
  switch (callingCode) {
    case COUNTRY_CALLING_CODES[CountryCodes.SG]:
      return CountryCodes.SG;
    case COUNTRY_CALLING_CODES[CountryCodes.ID]:
      return CountryCodes.ID;
    case COUNTRY_CALLING_CODES[CountryCodes.VN]:
      return CountryCodes.VN;
    case COUNTRY_CALLING_CODES[CountryCodes.MY]:
      return CountryCodes.MY;
    case COUNTRY_CALLING_CODES[CountryCodes.TW]:
      return CountryCodes.TW;
    default:
      return CountryCodes.SG;
  }
}

/**
 * Validates a full E.164 WhatsApp number string (e.g. "+6281234567890").
 * Matches the calling-code prefix against WhatsApp-enabled countries and
 * delegates to `validatePhoneNumber` for per-country rules.
 */
export function validateWhatsAppNumberString(
  phoneNumber: string | null | undefined
): boolean {
  if (!phoneNumber) return false;

  const options = getWhatsAppEnabledCallingCodeOptions();
  // Sort by calling code length descending so "+886" is matched before "+8"
  const sorted = [...options].sort(
    (a, b) => String(b.callingCode).length - String(a.callingCode).length
  );

  for (const opt of sorted) {
    const prefix = `+${opt.callingCode}`;
    if (phoneNumber.startsWith(prefix)) {
      return validatePhoneNumber({
        countryCode: opt.countryCode,
        phoneNumber,
      });
    }
  }

  return false;
}

export function removeCallingCode(phoneNumber: string): string {
  const normalized = phoneNumber.replace(/[\s\-()]/g, '');
  for (const code of Object.values(CALLING_CODES)) {
    if (normalized.startsWith(code)) {
      return normalized.slice(code.length);
    }
  }
  return normalized;
}
